import axios from 'axios';
export var S3FileFieldResultState;
(function (S3FileFieldResultState) {
    S3FileFieldResultState[S3FileFieldResultState["Aborted"] = 0] = "Aborted";
    S3FileFieldResultState[S3FileFieldResultState["Successful"] = 1] = "Successful";
    S3FileFieldResultState[S3FileFieldResultState["Error"] = 2] = "Error";
})(S3FileFieldResultState || (S3FileFieldResultState = {}));
export var S3FileFieldProgressState;
(function (S3FileFieldProgressState) {
    S3FileFieldProgressState[S3FileFieldProgressState["Initializing"] = 0] = "Initializing";
    S3FileFieldProgressState[S3FileFieldProgressState["Sending"] = 1] = "Sending";
    S3FileFieldProgressState[S3FileFieldProgressState["Finalizing"] = 2] = "Finalizing";
    S3FileFieldProgressState[S3FileFieldProgressState["Done"] = 3] = "Done";
})(S3FileFieldProgressState || (S3FileFieldProgressState = {}));
export default class S3FileFieldClient {
    /**
     * Create an S3FileFieldClient instance.
     *
     * @param options {S3FileFieldClientOptions} - A Object with all arguments.
     * @param options.baseUrl - The absolute URL to the Django server.
     * @param [options.apiConfig] - An axios configuration to use for Django API requests.
     *                              Can be extracted from an existing axios instance via `.defaults`.
     */
    constructor({ baseUrl, apiConfig = {}, }) {
        this.api = axios.create(Object.assign(Object.assign({}, apiConfig), { 
            // Add a trailing slash
            baseURL: baseUrl.replace(/\/?$/, '/') }));
    }
    /**
     * Initializes an upload.
     *
     * @param file - The file to upload.
     * @param fieldId - The Django field identifier.
     */
    async initializeUpload(file, fieldId) {
        const response = await this.api.post('upload-initialize/', {
            field_id: fieldId,
            file_name: file.name,
            file_size: file.size,
        });
        return response.data;
    }
    /**
     * Uploads all the parts in a file directly to an object store in serial.
     *
     * @param file - The file to upload.
     * @param parts - The list of parts describing how to break up the file.
     * @param onProgress - A callback for upload progress.
     */
    async uploadParts(// eslint-disable-line class-methods-use-this
    file, parts, onProgress) {
        const uploadedParts = [];
        let fileOffset = 0;
        for (const part of parts) {
            const chunk = file.slice(fileOffset, fileOffset + part.size);
            // eslint-disable-next-line no-await-in-loop
            const response = await axios.put(part.upload_url, chunk, {
                // eslint-disable-next-line @typescript-eslint/no-loop-func
                onUploadProgress: (e) => {
                    onProgress({
                        uploaded: fileOffset + e.loaded,
                        total: file.size,
                        state: S3FileFieldProgressState.Sending,
                    });
                },
            });
            uploadedParts.push({
                part_number: part.part_number,
                size: part.size,
                etag: response.headers.etag,
            });
            fileOffset += part.size;
        }
        return uploadedParts;
    }
    /**
     * Completes an upload.
     *
     * The object will exist in the object store after completion.
     *
     * @param multipartInfo - The information describing the multipart upload.
     * @param parts - The parts that were uploaded.
     */
    async completeUpload(multipartInfo, parts) {
        const response = await this.api.post('upload-complete/', {
            upload_signature: multipartInfo.upload_signature,
            upload_id: multipartInfo.upload_id,
            parts,
        });
        const { complete_url: completeUrl, body } = response.data;
        // Send the CompleteMultipartUpload operation to S3
        await axios.post(completeUrl, body, {
            headers: {
                // By default, Axios sets "Content-Type: application/x-www-form-urlencoded" on POST
                // requests. This causes AWS's API to interpret the request body as additional parameters
                // to include in the signature validation, causing it to fail.
                // So, do not send this request with any Content-Type, as that is what's specified by the
                // CompleteMultipartUpload docs.
                // Unsetting default headers via "transformRequest" is awkward (since the headers aren't
                // flattened), so this is actually; the most straightforward way; the null value is passed
                // through to XMLHttpRequest, then ignored.
                'Content-Type': null,
            },
        });
    }
    /**
     * Finalizes an upload.
     *
     * This will only succeed if the object is already present in the object store.
     *
     * @param multipartInfo - Signed information returned from /upload-complete/.
     */
    async finalize(multipartInfo) {
        const response = await this.api.post('finalize/', {
            upload_signature: multipartInfo.upload_signature,
        });
        return response.data.field_value;
    }
    /**
     * Uploads a file using multipart upload.
     *
     * @param file - The file to upload.
     * @param fieldId - The Django field identifier.
     * @param [onProgress] - A callback for upload progress.
     */
    async uploadFile(file, fieldId, onProgress = () => { }) {
        onProgress({ state: S3FileFieldProgressState.Initializing });
        const multipartInfo = await this.initializeUpload(file, fieldId);
        onProgress({ state: S3FileFieldProgressState.Sending, uploaded: 0, total: file.size });
        const parts = await this.uploadParts(file, multipartInfo.parts, onProgress);
        onProgress({ state: S3FileFieldProgressState.Finalizing });
        await this.completeUpload(multipartInfo, parts);
        const value = await this.finalize(multipartInfo);
        onProgress({ state: S3FileFieldProgressState.Done });
        return {
            value,
            state: S3FileFieldResultState.Successful,
        };
    }
}
//# sourceMappingURL=client.js.map